import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./ReportsPage.css";
import { LogoutButton } from "../Common/LogoutButton";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { apiFetch } from "../../utils/api";
import Loader from "../Common/Loader";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const ReportsPage = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("forkliftNo");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (!user) {
      navigate("/auth", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const data = await apiFetch(`/api/reports/rides`);
        const { rideReports } = data;

        const formattedReports = rideReports.map((report) => ({
          driverName: report.DRIVER_NAME || " ",
          requesterName: report.REQUESTER_NAME || "Unknown",
          vehicleNo: report.VEHICLE_NUMBER || "Unknown",
          forkliftNo: report.FORKLIFT_NUMBER || "Unknown",
          vehicleType: report.VEHICLE_TYPE || "Unknown",
          startTime: report.START_TIME,
          materialPickTime: report.MATERIAL_PICK_TIME,
          endRideTime: report.END_TIME,
          gapPickDrop: report.GAP_PICK_DROP,
          gapPickStart: report.GAP_PICK_START,
          lastMaintenanceDate: report.LAST_MAINTENANCE_DATE || "N/A",
          maintenanceStatus: report.MAINTENANCE_STATUS || "N/A",
        }));

        setReports(formattedReports);
      } catch (error) {
        console.error("❌ Error fetching reports:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when search or filter changes
  }, [searchTerm, filterType]);

  const getMaintenanceStatusBadge = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return <span className="status-badge pending">Pending</span>;
      case "accepted":
        return <span className="status-badge accepted">Accepted</span>;
      case "completed":
        return <span className="status-badge completed">Completed</span>;
      case "rejected":
        return <span className="status-badge rejected">Rejected</span>;
      default:
        return <span className="status-badge">N/A</span>;
    }
  };

  const getVehicleTypeBadge = (type) => {
    switch (type.toLowerCase()) {
      case "forklift":
        return <span className="vehicle-badge forklift">Forklift</span>;
      case "crane":
        return <span className="vehicle-badge crane">Crane</span>;
      case "cherry picker":
        return (
          <span className="vehicle-badge cherry-picker">Cherry Picker</span>
        );
      default:
        return <span className="vehicle-badge">N/A</span>;
    }
  };

  const filteredReports = reports.filter((report) => {
    const matchesSearch =
      filterType === "date"
        ? true
        : report[filterType]?.toLowerCase().includes(searchTerm.toLowerCase());

    const reportDate = report.lastMaintenanceDate
      ? new Date(report.lastMaintenanceDate)
      : null;
    const inRange =
      (!startDate && !endDate) ||
      (reportDate &&
        (!startDate || reportDate >= startDate) &&
        (!endDate || reportDate <= endDate));

    return matchesSearch && inRange;
  });

  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentPageReports = filteredReports.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const downloadCSV = () => {
    if (!reports.length) return;

    const header = Object.keys(reports[0]).join(",");
    const rows = reports.map((report) =>
      Object.values(report)
        .map((value) => `"${value}"`) // wrap values in quotes
        .join(",")
    );
    const csvContent = [header, ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "ride_reports.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="reports-container">
      {/* Main Heading */}
      <h1 className="reports-main-heading">Reports</h1>

      {/* Header Bar */}
      <div className="reports-header">
        <button
          onClick={() => navigate("/maintenance-head")}
          className="back-btn"
        >
          ← Back
        </button>
        <LogoutButton />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          margin: "10px 0",
        }}
      >
        <button onClick={downloadCSV} className="download-btn">
          Download CSV
        </button>
      </div>

      {/* Search Input with Filter */}
      <div className="search-bar">
        <select
          className="search-select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="vehicleNo">Vehicle Number</option>
          <option value="forkliftNo">Forklift Number</option>
          <option value="vehicleType">Vehicle Type</option>
          <option value="date">Date</option>
          <option value="driverName">Driver Name</option>
        </select>

        {filterType === "date" && (
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              placeholderText="Start Date"
              isClearable
            />
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              placeholderText="End Date"
              isClearable
            />
          </div>
        )}

        <input
          type="text"
          placeholder={`Search by ${
            filterType === "vehicleNo"
              ? "Vehicle Number"
              : filterType === "vehicleType"
              ? "Vehicle Type"
              : filterType === "driverName"
              ? "Driver Name"
              : "Forklift Number"
          }`}
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-indicator">
            <Loader />{" "}
          </div>
        ) : (
          <table className="reports-table">
            <thead>
              <tr>
                <th>Vehicle Number</th>
                <th>Driver Name</th>
                <th>Requester Name</th>
                <th>Forklift Number</th>
                <th>Vehicle Type</th>
                <th>Start Time</th>
                <th>Material Pick Time</th>
                <th>End Ride Time</th>
                <th>Gap (Pick & Drop)</th>
                <th>Gap (Pick & Start)</th>
                <th>Last Maintenance Date</th>
                <th>Maintenance Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredReports.length > 0 ? (
                currentPageReports.map((report, index) => (
                  <tr key={index}>
                    <td>{report.vehicleNo}</td>
                    <td>{report.driverName}</td>
                    <td>{report.requesterName}</td>
                    <td>{report.forkliftNo}</td>
                    <td>{getVehicleTypeBadge(report.vehicleType)}</td>
                    <td>{report.startTime}</td>
                    <td>{report.materialPickTime}</td>
                    <td>{report.endRideTime}</td>
                    <td>{report.gapPickDrop} mins</td>
                    <td>{report.gapPickStart} mins</td>
                    <td>{report.lastMaintenanceDate}</td>
                    <td>
                      {getMaintenanceStatusBadge(report.maintenanceStatus)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10">No reports available</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && filteredReports.length > itemsPerPage && (
        <div className="pagination-controls">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            ← Previous
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}

      {/* Scroll indicator (optional) */}
      {filteredReports.length > 10 && (
        <div className="scroll-indicator">Scroll down to see more reports</div>
      )}
    </div>
  );
};

export default ReportsPage;
